import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { authService } from '../services';
import api, { refreshAccessToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for storage changes (for account suspension updates from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'msk_user_data') {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
      }
    };

    // Listen for user suspended event (same tab)
    const handleUserSuspended = (e) => {
      console.log('User suspended event received:', e.detail);
      if (e.detail) {
        setUser(e.detail);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userSuspended', handleUserSuspended);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userSuspended', handleUserSuspended);
    };
  }, []);

  // Silently refresh the access token every 10 minutes while the user is logged in.
  // This prevents logout caused by the 15-minute token expiry during active sessions.
  useEffect(() => {
    if (!isAuthenticated) return;

    const TEN_MINUTES = 10 * 60 * 1000;
    const intervalId = setInterval(async () => {
      const success = await refreshAccessToken();
      if (!success) {
        // Refresh token itself has expired — log out cleanly
        setUser(null);
        setIsAuthenticated(false);
      }
    }, TEN_MINUTES);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  /**
   * Register new user
   */
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const result = await authService.register(userData);
      
      if (result.success) {
        setUser(result.data);
        setIsAuthenticated(true);
        return { success: true, data: result.data };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: error.message || 'حدث خطأ أثناء إنشاء الحساب' };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const result = await authService.login(email, password);
      
      if (result.success) {
        setUser(result.data);
        setIsAuthenticated(true);
        return { success: true, data: result.data };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: error.message || 'حدث خطأ أثناء تسجيل الدخول' };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Logout from all devices
   */
  const logoutAll = useCallback(async () => {
    try {
      await authService.logoutAll();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (profileData) => {
    try {
      // TODO: Add API call to update profile on server
      const updated = authService.updateStoredUser(profileData);
      if (updated) {
        setUser(updated);
        return { success: true, data: updated };
      }
      return { success: false, error: 'فشل تحديث الملف الشخصي' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Update user settings
   */
  const updateSettings = useCallback(async (settings) => {
    try {
      // TODO: Add API call to update settings on server
      const currentUser = authService.getStoredUser();
      const updated = authService.updateStoredUser({
        ...currentUser,
        settings: { ...currentUser?.settings, ...settings }
      });
      if (updated) {
        setUser(updated);
        return { success: true, data: updated };
      }
      return { success: false, error: 'فشل تحديث الإعدادات' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Refresh user data from the API and update localStorage + context state.
   * Used after operations that change accountType or parentUser (e.g. accepting an invite).
   */
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/user/profile');
      if (res?.data?.user) {
        const freshUser = res.data.user;
        authService.updateStoredUser(freshUser);
        setUser(freshUser);
      }
    } catch {
      // Fallback to localStorage if the API call fails
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        setUser(storedUser);
      }
    }
  }, []);

  // Memoized context value
  const value = useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    logoutAll,
    updateProfile,
    updateSettings,
    refreshUser
  }), [user, isAuthenticated, loading, login, register, logout, logoutAll, updateProfile, updateSettings, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
